import { useState } from 'react';
import { Button, Icon, TextInput } from '@forecastify/ui';
import { useT } from '../i18n';

export interface PickerItem {
  readonly id: string;
  readonly label: string;
  /** Muted, at the row's right end — a category, a code. */
  readonly hint?: string;
  /** A small swatch before the label. */
  readonly color?: string;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Search a list somebody already holds, and pick one — anchored to a button. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `TaskPicker`'s panel for a list that is already in hand rather than searched on the server: the
 * same floating box under the button that opened it, the same search field, hint line and rows, so
 * adding a skill to a person and linking a task read as one gesture.
 *
 * ⚠️ **`absolute` against the trigger's `relative` wrapper**, right-anchored, for `TaskPicker`'s
 * reasons: it floats over the section rather than pushing it down, and a portal would need its own
 * dismiss and position tracking to land in the same place. Escape closes it; there is no backdrop.
 */
export function ItemPicker({
  title,
  hint,
  items,
  onPick,
  onClose,
}: {
  /** Already translated — the panel's name and the field's placeholder. */
  readonly title: string;
  /** Already translated — what the list holds and what it does not. */
  readonly hint?: string;
  readonly items: readonly PickerItem[];
  readonly onPick: (id: string) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const shown =
    needle === ''
      ? items
      : items.filter((item) => `${item.label} ${item.hint ?? ''}`.toLowerCase().includes(needle));

  return (
    <div
      role="search"
      aria-label={title}
      className="border-input bg-popover absolute top-full right-0 z-20 mt-1 flex w-[340px] max-w-[calc(100vw-2rem)] flex-col gap-2 rounded-lg border p-2 normal-case shadow-lg"
    >
      <div className="flex items-center gap-2">
        <TextInput
          autoFocus
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            }
          }}
          placeholder={title}
          aria-label={title}
          className="flex-1"
        />
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={14} />
        </Button>
      </div>

      {hint === undefined ? null : (
        <p className="text-muted-foreground text-[11.5px] leading-relaxed tracking-normal">
          {hint}
        </p>
      )}

      <div className="max-h-[240px] overflow-y-auto">
        {shown.length === 0 ? (
          <p className="text-muted-foreground px-2 py-4 text-center text-[12px]">
            {t('task.picker.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {shown.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="hover:bg-accent/50 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left"
                  onClick={() => {
                    onPick(item.id);
                  }}
                >
                  {item.color === undefined ? null : (
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: item.color }}
                    />
                  )}
                  <span className="text-foreground min-w-0 flex-1 truncate text-[12.5px]">
                    {item.label}
                  </span>
                  {item.hint === undefined ? null : (
                    <span className="text-muted-foreground shrink-0 text-[11.5px]">
                      {item.hint}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
